import { generateWorkloadNameWithEnv, splitAndTrimComma } from "yam-operator/util"
import { ExecuteContext, K8SResourceType, KVString, OperateFunction, PlanContext } from "yam-types"
import { Settings } from "../settings.d"
import { ApplicationSecret } from "../types.d"
import { patchVolumeAndEnv, randomAlphabet, randomAlphabetNumeric, randomHex, randomNumeric, randomRSAPrivateKey as randomRSAKeyPair } from "./util"

const secretOperator: OperateFunction<ApplicationSecret> = async (plan, diff, settings: Settings) => {
  // generate new secret if needed, secret-operator does NOT support key rotation
  if (diff.hasNew || diff.hasModified) {
    const changedSecrets = [...diff.newItems, ...diff.modifiedItems.map(x => x.current)]
    changedSecrets.forEach(item => {
      if (item.kms && item.kms.provider) {
        // TODO integration with cloud secret providers
        plan.log.info(`secrets of '${item.name}' are provided by '${item.kms.provider}', skip generating and mounting.`)
        return
      }
      // For native Kubernetes secrets, check and create inside engine
      const secrets = {} as KVString
      const secName = plan.data.workloadName + "-" + item.name
      item.secrets?.forEach(key => {
        if (key.generateRandom) {
          const keyRaw = generateSecretKey(key.generateRandom.length, key.generateRandom.format)
          if (typeof keyRaw === "string") {
            secrets[key.name] = keyRaw.toString()
          } else {
            splitAndTrimComma(key.name).forEach((subKey, idx) => {
              secrets[subKey] = keyRaw[idx].toString()
            })
          }
        }
      })
      if (Object.keys(secrets).length > 0) {
        plan.action(async (ctx) => {
          const existing = await ctx.k8sClient.find({
            kind: K8SResourceType.Secret,
            name: secName
          })
          if (existing?.length > 0) {
            ctx.log.info(`secret ${secName} already exists, skip secret generating`)
            return
          }
          await ctx.k8sClient.saveConf({
            secret: true,
            data: secrets,
            name: secName,
            metadata: {
              annotations: settings.secretAnnotations
            }
          })
        }, "create-new-secret:" + item.name)
      }
    })
  }
  for (const item of diff.currentItems) {
    let secretName = plan.data.workloadName + "-" + item.name
    if (item.refer) {
      const refName = item.refer.secret
      if (!refName) {
        plan.log.warn(`no referred secret specified for '${item.name}'`)
        continue
      }
      if (item.refer.isNative) {
        secretName = refName
      } else {
        secretName = (
          plan.data.multiEnvMode
            ? generateWorkloadNameWithEnv(item.refer.app as string, plan.data.envName)
            : item.refer.app
        ) + '-' + refName
      }
    }

    plan.action(async (ctx) => {
      if (item.kms && item.kms.provider) {
        // inject metadata for 3rd party secret providers like AWS SecretManager, Vault, etc.
        injectSecretProvider(ctx, plan, item)
        return
      } else {
        // handle volume/env mount for k8s secrets

        patchVolumeAndEnv(ctx, item, plan, secretName, "secret")
      }
    }, `sync-secret:${item.name}`)
  }
}

const injectSecretProvider = (ctx: ExecuteContext, plan: PlanContext, item: ApplicationSecret) => {
  if (!item.kms) {
    return
  }
  switch (item.kms.provider) {
    case "aws":
      // inject roleArn into serviceAccount for AWS EKS, app could fetch secret by calling AWS STS and SecretManager
      // env AWS_ROLE_ARN and AWS_WEB_IDENTITY_TOKEN_FILE will be injected by aws/amazon-eks-pod-identity-webhook
      // ref: https://aws.amazon.com/blogs/opensource/introducing-fine-grained-iam-roles-service-accounts
      ctx.mergeToYaml({
        name: plan.data.serviceAccountName,
        jsonPath: 'metadata',
        defer: true,
        content: {
          annotations: {
            "eks.amazonaws.com/role-arn": item.kms.iamRole
          }
        }
      })
      break
    case "vault":
      // TODO support vault secret injection
      break
  }
}


type KeyGenerationFormat = "numeric" | "alphabet" | "alphabetNumeric" | "hex" | "rsaKeyPair"

const generateSecretKey = (length: number, format: KeyGenerationFormat): string | string[] => {
  switch (format) {
    case "alphabet":
      return randomAlphabet(length)
    case "numeric":
      return randomNumeric(length)
    case "alphabetNumeric":
      return randomAlphabetNumeric(length)
    case "hex":
      return randomHex(length)
    case "rsaKeyPair":
      return randomRSAKeyPair(length)
    default:
      throw new Error('key generator format not supported')
  }
}

export default secretOperator